import { createRootRoute, createRoute, createRouter, lazyRouteComponent } from "@tanstack/react-router";
import { Casca } from "./componentes/Casca";
import { busca, data, opcao, pagina, texto, uuid } from "./lib/busca";
import { SECOES_EM_CONSTRUCAO } from "./menu";
import { PaginaEmConstrucao, PaginaErro, PaginaNaoEncontrada } from "./paginas/Outras";

// Cada página vira um arquivo separado no build e só é baixada ao abrir a rota.
const PaginaInicio = lazyRouteComponent(() => import("./paginas/Inicio"), "PaginaInicio");
const PaginaAgenda = lazyRouteComponent(() => import("./paginas/Agenda"), "PaginaAgenda");
const PaginaCadastros = lazyRouteComponent(() => import("./paginas/Cadastros"), "PaginaCadastros");
const PaginaPacientes = lazyRouteComponent(() => import("./paginas/Pacientes"), "PaginaPacientes");
const PaginaPacienteFicha = lazyRouteComponent(() => import("./paginas/PacienteFicha"), "PaginaPacienteFicha");
const PaginaNovoPaciente = lazyRouteComponent(() => import("./paginas/PacienteFormulario"), "PaginaNovoPaciente");
const PaginaEditarPaciente = lazyRouteComponent(() => import("./paginas/PacienteFormulario"), "PaginaEditarPaciente");
const PaginaConta = lazyRouteComponent(() => import("./paginas/Conta"), "PaginaConta");
const PaginaCobrar = lazyRouteComponent(() => import("./paginas/Cobrar"), "PaginaCobrar");
const PaginaCaixa = lazyRouteComponent(() => import("./paginas/Caixa"), "PaginaCaixa");
const PaginaMeuDinheiro = lazyRouteComponent(() => import("./paginas/MeuDinheiro"), "PaginaMeuDinheiro");
const PaginaContasReceber = lazyRouteComponent(() => import("./paginas/Contas"), "PaginaContasReceber");
const PaginaContasPagar = lazyRouteComponent(() => import("./paginas/Contas"), "PaginaContasPagar");
const PaginaEstoque = lazyRouteComponent(() => import("./paginas/Estoque"), "PaginaEstoque");
const PaginaEntradaNota = lazyRouteComponent(() => import("./paginas/EntradaNota"), "PaginaEntradaNota");
const PaginaProntuario = lazyRouteComponent(() => import("./paginas/Prontuario"), "PaginaProntuario");
const PaginaAtendimento = lazyRouteComponent(() => import("./paginas/Atendimento"), "PaginaAtendimento");
const PaginaProtocolo = lazyRouteComponent(() => import("./paginas/Protocolo"), "PaginaProtocolo");
const PaginaUsuarios = lazyRouteComponent(() => import("./paginas/Usuarios"), "PaginaUsuarios");
const PaginaAcessos = lazyRouteComponent(() => import("./paginas/Acessos"), "PaginaAcessos");
const PaginaSeguranca = lazyRouteComponent(() => import("./paginas/Seguranca"), "PaginaSeguranca");
const PaginaWhatsapp = lazyRouteComponent(() => import("./paginas/WhatsApp"), "PaginaWhatsapp");
const PaginaConversas = lazyRouteComponent(() => import("./paginas/Conversas"), "PaginaConversas");
const PaginaMensagensAutomaticas = lazyRouteComponent(() => import("./paginas/MensagensAutomaticas"), "PaginaMensagensAutomaticas");
const PaginaOrcamentos = lazyRouteComponent(() => import("./paginas/Orcamentos"), "PaginaOrcamentos");
const PaginaRelatorios = lazyRouteComponent(() => import("./paginas/Relatorios"), "PaginaRelatorios");
const PaginaEmpresas = lazyRouteComponent(() => import("./paginas/Empresas"), "PaginaEmpresas");
const PaginaConfiguracaoInicial = lazyRouteComponent(() => import("./paginas/ConfiguracaoInicial"), "PaginaConfiguracaoInicial");
const PaginaNotasFiscais = lazyRouteComponent(() => import("./paginas/NotasFiscais"), "PaginaNotasFiscais");
const PaginaConferir = lazyRouteComponent(() => import("./paginas/Conferir"), "PaginaConferir");
const PaginaResponder = lazyRouteComponent(() => import("./paginas/Responder"), "PaginaResponder");

const raiz = createRootRoute({ component: Casca, notFoundComponent: PaginaNaoEncontrada });

const inicio = createRoute({ getParentRoute: () => raiz, path: "/", component: PaginaInicio });

const pacientes = createRoute({ getParentRoute: () => raiz, path: "/pacientes" });

const buscaPacientes = busca({
  busca: texto(),
  pagina: pagina,
  recorte: opcao(["com_agendamento", "sem_retorno", "com_saldo", "aniversariantes", "arquivados"]),
  ordem: opcao(["ultima_visita", "cadastro"]),
  etiqueta: texto(),
});

const pacientesLista = createRoute({
  getParentRoute: () => pacientes,
  path: "/",
  validateSearch: buscaPacientes,
  component: PaginaPacientes,
});
const pacienteNovo = createRoute({ getParentRoute: () => pacientes, path: "/novo", component: PaginaNovoPaciente });
const pacienteFicha = createRoute({ getParentRoute: () => pacientes, path: "/$id", component: PaginaPacienteFicha });
const pacienteEditar = createRoute({ getParentRoute: () => pacientes, path: "/$id/editar", component: PaginaEditarPaciente });

const agenda = createRoute({
  getParentRoute: () => raiz,
  path: "/agenda",
  validateSearch: busca({
    data: data,
    visao: opcao(["dia", "semana"]),
    /** Abre o formulário já com este paciente (botão "Agendar" da ficha). */
    agendarPaciente: uuid,
    /** Com o paciente: profissional e procedimento já escolhidos (agendar retorno do atendimento). */
    agendarProfissional: uuid,
    agendarProcedimento: uuid,
    /** Abre o formulário de agendamento vazio (botão "Novo" da barra superior). */
    novo: opcao(["agendamento"]),
  }),
  component: PaginaAgenda,
});

const cadastros = createRoute({
  getParentRoute: () => raiz,
  path: "/cadastros",
  validateSearch: busca({
    aba: opcao(["profissionais", "salas", "procedimentos", "listas"]),
    /** Abre o formulário de cadastro novo (vindo da Configuração inicial). */
    abrir: opcao(["novo"]),
  }),
  component: PaginaCadastros,
});

const prontuario = createRoute({ getParentRoute: () => raiz, path: "/prontuario" });
const prontuarioLista = createRoute({
  getParentRoute: () => prontuario,
  path: "/",
  validateSearch: busca({ aba: opcao(["atendimentos", "documentos", "protocolos", "termos"]) }),
  component: PaginaProntuario,
});
const prontuarioProtocolo = createRoute({ getParentRoute: () => prontuario, path: "/protocolos/$id", component: PaginaProtocolo });
const prontuarioAtendimento = createRoute({ getParentRoute: () => prontuario, path: "/$id", component: PaginaAtendimento });

const estoque = createRoute({
  getParentRoute: () => raiz,
  path: "/estoque",
  validateSearch: busca({
    aba: opcao(["posicao", "alertas", "movimentos", "fornecedores", "locais"]),
    /** Movimentações de um lote só (recall: o lote pode já estar sem saldo). */
    lote: uuid,
    /** Abre o cadastro de produto novo (vindo da Configuração inicial). */
    abrir: opcao(["novo"]),
  }),
  component: PaginaEstoque,
});
const entradaNota = createRoute({ getParentRoute: () => raiz, path: "/entrada-nota", component: PaginaEntradaNota });

const cobrar = createRoute({
  getParentRoute: () => raiz,
  path: "/cobrar",
  validateSearch: busca({
    pacienteId: uuid,
    atendimentoId: uuid,
    procedimentoId: uuid,
    /** Profissional do atendimento: o preço do procedimento é o dele, quando tiver. */
    profissionalId: uuid,
    /** Cobrança de um orçamento: itens e desconto vêm dele. */
    orcamentoId: uuid,
  }),
  component: PaginaCobrar,
});
const caixa = createRoute({ getParentRoute: () => raiz, path: "/caixa", component: PaginaCaixa });
const financeiro = createRoute({
  getParentRoute: () => raiz,
  path: "/financeiro",
  validateSearch: busca({ aba: opcao(["resumo", "comissoes", "configuracao"]) }),
  component: PaginaMeuDinheiro,
});
const contasReceber = createRoute({ getParentRoute: () => raiz, path: "/contas-a-receber", component: PaginaContasReceber });
const contasPagar = createRoute({ getParentRoute: () => raiz, path: "/contas-a-pagar", component: PaginaContasPagar });

const conta = createRoute({ getParentRoute: () => raiz, path: "/conta", validateSearch: busca({ abrir: opcao(["2fa"]) }), component: PaginaConta });
const usuarios = createRoute({ getParentRoute: () => raiz, path: "/usuarios", validateSearch: busca({ abrir: opcao(["novo"]) }), component: PaginaUsuarios });
const acessos = createRoute({
  getParentRoute: () => raiz,
  path: "/acessos",
  validateSearch: busca({ aba: opcao(["prontuario"]), paciente: uuid }),
  component: PaginaAcessos,
});
const seguranca = createRoute({
  getParentRoute: () => raiz,
  path: "/seguranca",
  // Volta da conexão do e-mail com o Google ou a Microsoft.
  validateSearch: busca({ email: opcao(["conectado", "erro"]), motivo: texto(300) }),
  component: PaginaSeguranca,
});

const whatsapp = createRoute({
  getParentRoute: () => raiz,
  path: "/whatsapp",
  validateSearch: busca({ abrir: opcao(["novo", "feriados"]) }),
  component: PaginaWhatsapp,
});
const conversas = createRoute({
  getParentRoute: () => raiz,
  path: "/conversas",
  validateSearch: busca({
    c: uuid,
    /** Atalho de resposta rápida que entra já escrito na conversa aberta. */
    resposta: texto(30, /^[a-z0-9-]{2,30}$/),
  }),
  component: PaginaConversas,
});

const mensagensAutomaticas = createRoute({
  getParentRoute: () => raiz,
  path: "/mensagens-automaticas",
  validateSearch: busca({ aba: opcao(["reguas", "respostas", "historico"]) }),
  component: PaginaMensagensAutomaticas,
});

const orcamentos = createRoute({
  getParentRoute: () => raiz,
  path: "/orcamentos",
  validateSearch: busca({ aba: opcao(["interessados", "orcamentos"]) }),
  component: PaginaOrcamentos,
});

const relatorios = createRoute({
  getParentRoute: () => raiz,
  path: "/relatorios",
  validateSearch: busca({
    aba: opcao(["financeiro", "agenda", "pacientes", "margem", "estoque"]),
    de: data,
    ate: data,
  }),
  component: PaginaRelatorios,
});

const empresas = createRoute({
  getParentRoute: () => raiz,
  path: "/empresas",
  validateSearch: busca({ abrir: opcao(["empresa", "unidade"]) }),
  component: PaginaEmpresas,
});
const configuracaoInicial = createRoute({ getParentRoute: () => raiz, path: "/configuracao", component: PaginaConfiguracaoInicial });

const notasFiscais = createRoute({
  getParentRoute: () => raiz,
  path: "/notas-fiscais",
  validateSearch: busca({ aba: opcao(["emitir", "emitidas"]) }),
  component: PaginaNotasFiscais,
});

const conferir = createRoute({ getParentRoute: () => raiz, path: "/conferir/$id", validateSearch: busca({ c: texto() }), component: PaginaConferir });

const responder = createRoute({ getParentRoute: () => raiz, path: "/responder/$token", component: PaginaResponder });

const emConstrucao = SECOES_EM_CONSTRUCAO.map((s) =>
  createRoute({ getParentRoute: () => raiz, path: s.para, component: PaginaEmConstrucao }),
);

const arvore = raiz.addChildren([
  inicio,
  agenda,
  cadastros,
  prontuario.addChildren([prontuarioLista, prontuarioProtocolo, prontuarioAtendimento]),
  estoque,
  entradaNota,
  cobrar,
  caixa,
  financeiro,
  contasReceber,
  contasPagar,
  conta,
  usuarios,
  acessos,
  seguranca,
  whatsapp,
  conversas,
  mensagensAutomaticas,
  orcamentos,
  relatorios,
  empresas,
  configuracaoInicial,
  notasFiscais,
  conferir,
  responder,
  pacientes.addChildren([pacientesLista, pacienteNovo, pacienteFicha, pacienteEditar]),
  ...emConstrucao,
]);

export const roteador = createRouter({ routeTree: arvore, defaultPreload: "intent", defaultErrorComponent: PaginaErro, scrollRestoration: true });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof roteador;
  }
}
